import { format } from 'date-fns';
import { CardConfig, AggregationType, ChartType } from '../../types';

export interface ChartSerializerConfig {
  xAxisFieldId?: string;
  yAxisFieldId?: string;
  zAxisFieldId?: string;
  seriesAxisFieldId?: string;
  groupAxisFieldId?: string;
  aggregationType?: AggregationType;
  isAggregated?: boolean;
  kpiMode?: AggregationType;
  calcType?: string;
  dateGrouping?: 'day' | 'week' | 'month' | 'bimonth' | 'semester' | 'year';
}

export interface SerializedDataPoint {
  [key: string]: any;
}

export interface ChartSerializerResult {
  data: SerializedDataPoint[];
  seriesKeys: string[];
  xAxisKey: string;
  zAxisKey?: string;
  kpiValue?: number | string | null;
}

// Helpers de Tempo para Agregação
const isTimeString = (val: any): boolean => {
  if (typeof val !== 'string') return false;
  return /^(\d{2,}|\d{1,2}):\d{2}(:\d{2})?$/.test(val.trim());
};

const timeToSeconds = (timeStr: string): number => {
  if (typeof timeStr !== 'string') return 0;
  const parts = timeStr.trim().split(':').map(Number);
  const h = parts[0] || 0;
  const m = parts[1] || 0;
  const s = parts[2] || 0;
  return h * 3600 + m * 60 + s;
};

const secondsToTime = (totalSeconds: number): string => {
  const sign = totalSeconds < 0 ? "-" : "";
  const absSeconds = Math.abs(Math.round(totalSeconds));
  const h = Math.floor(absSeconds / 3600);
  const m = Math.floor((absSeconds % 3600) / 60);
  const s = absSeconds % 60;
  const hh = h.toString().padStart(2, '0');
  const mm = m.toString().padStart(2, '0');
  const ss = s.toString().padStart(2, '0');
  return `${sign}${hh}:${mm}:${ss}`;
};

export function useChartSerializer(
  responses: any[],
  config: ChartSerializerConfig,
  vizType: ChartType,
  allFields?: any[],
  forms?: any[]
): ChartSerializerResult {
  // Helper to extract value considering GLOBAL aliases (TICKET 2)
  const getFieldValue = (r: any, fieldId: string | undefined) => {
    if (!fieldId) return undefined;
    if (fieldId.startsWith('GLOBAL::')) {
       const globalField = allFields?.find(f => f.fieldId === fieldId);
       const linkedIds = globalField?.linkedFieldIds || [];
       for (const lid of linkedIds) {
          // Linked IDs from legacy Global Aliases might still be raw or composite
          // We assume Global Aliases are evaluated as-is or we can apply the same split logic
          let lReqFormId: string | null = null;
          let lReqFieldId = lid;
          if (lid.includes('::')) {
              [lReqFormId, lReqFieldId] = lid.split('::');
          }
          if (lReqFormId && lReqFormId !== 'global') {
              const rFormId = r.formId || r.form_id;
              if (rFormId !== lReqFormId) continue;
          }
          const val = r.data?.[lReqFieldId]?.value;
          if (val !== undefined && val !== null && val !== '') return val;
       }
       return undefined;
    }

    let reqFormId: string | null = null;
    let reqFieldId = fieldId;
    // Parameter IDs start with 'param_'. They don't use '::' for looking up data.
    if (fieldId.includes('::') && !fieldId.startsWith('param_')) {
       [reqFormId, reqFieldId] = fieldId.split('::');
    }

    if (reqFormId && reqFormId !== 'global') {
        const rowFormId = r.formId || r.form_id;
        if (rowFormId !== reqFormId) return undefined;
    }

    return r.data?.[reqFieldId]?.value;
  };

  // Pre-filter responses to avoid N/A and stretched axes (TICKET 3)
  const isMetaField = (id: string | undefined) => !id || ['ROOT_COUNT', 'startedAt', 'respondentName', 'formId'].includes(id);

  const hasDataForField = (r: any, fieldId: string | undefined) => {
    if (isMetaField(fieldId)) return true;
    const val = getFieldValue(r, fieldId);
    return val !== undefined && val !== null && val !== '';
  };

  const validResponses = responses.filter(r => {
    if (!hasDataForField(r, config.xAxisFieldId)) return false;
    if (!hasDataForField(r, config.yAxisFieldId)) return false;
    if (!hasDataForField(r, config.zAxisFieldId)) return false;
    if (!hasDataForField(r, config.seriesAxisFieldId)) return false;
    if (vizType === 'scatter' && !hasDataForField(r, config.groupAxisFieldId)) return false;
    return true;
  });

  // Helper to calculate statistics
  const calculateStat = (records: any[], fieldId: string, agg: AggregationType | string) => {
    if (agg === 'COUNT' || agg === 'count' || fieldId === 'ROOT_COUNT') return records.length;

    // extração dos valores brutos
    const vals = records
      .map(r => getFieldValue(r, fieldId))
      .filter(v => v !== undefined && v !== null && v !== '');

    if (agg === 'COUNT_UNIQUE' || agg === 'count_unique') {
      return new Set(vals.map(String)).size;
    }

    const isTime = vals.length > 0 && isTimeString(vals[0]);
    let nums: number[] = [];
    if (isTime) {
       nums = vals.map(v => isTimeString(v) ? timeToSeconds(v) : 0);
    } else {
       nums = vals.map(Number).filter(n => !isNaN(n));
    }

    if (agg === 'LATEST') {
        if (vals.length === 0) return null;
        return vals[vals.length - 1]; // Assume records are sorted chronologically if applicable, or just take the last response
    }

    if (nums.length === 0) return 0;

    let resultNum = 0;
    switch (agg) {
      case 'SUM': case 'sum': resultNum = nums.reduce((a, b) => a + b, 0); break;
      case 'AVG': case 'avg': resultNum = nums.reduce((a, b) => a + b, 0) / nums.length; break;
      case 'MIN': case 'min': resultNum = Math.min(...nums); break;
      case 'MAX': case 'max': resultNum = Math.max(...nums); break;
      case 'delta': resultNum = Math.max(...nums) - Math.min(...nums); break;
      case 'median': {
        const sorted = [...nums].sort((a, b) => a - b);
        const mid = Math.floor(sorted.length / 2);
        resultNum = sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
        break;
      }
      case 'mode': {
        const counts: Record<string, number> = {};
        let maxCount = 0;
        let modeVal = nums[0];
        for (const num of nums) {
          counts[num] = (counts[num] || 0) + 1;
          if (counts[num] > maxCount) {
            maxCount = counts[num];
            modeVal = num;
          }
        }
        resultNum = modeVal;
        break;
      }
      default: resultNum = nums.reduce((a, b) => a + b, 0); // Default to SUM if numeric
    }

    if (isTime) {
       return secondsToTime(resultNum);
    }
    return resultNum;
  };

  // Determine Aggregation Type
  // Provide a progressive fallback logic for legacy cards
  let activeAggregation: AggregationType | string = config.aggregationType || config.calcType || 'SUM';
  if (!config.aggregationType && config.isAggregated === true) {
      activeAggregation = 'SUM'; // Fallback
  }
  if (!config.isAggregated && config.aggregationType === 'NONE') {
      activeAggregation = 'NONE';
  }

  // Handle KPI
  if (vizType === 'kpi') {
    const kpiField = config.xAxisFieldId || config.yAxisFieldId || 'ROOT_COUNT'; // Usually yAxis holds the value or fieldId in legacy
    const agg = config.kpiMode || activeAggregation || 'COUNT';
    const val = calculateStat(validResponses, kpiField, agg);
    return { data: [], seriesKeys: [], xAxisKey: '', kpiValue: val };
  }

  // Handle Scatter
  if (vizType === 'scatter') {
    const groups: Record<string, { records: any[], sortKey: number | string }> = {};
    const groupField = config.groupAxisFieldId || 'respondentName';

    validResponses.forEach(r => {
      let gVal: any;
      if (groupField === 'respondentName') gVal = r.respondentName;
      else if (groupField === 'formId') gVal = forms?.find(x => x.id === r.formId)?.title || r.formId;
      else gVal = getFieldValue(r, groupField);

      if (gVal === undefined || gVal === null || gVal === '') gVal = 'N/A';
      const key = String(gVal);
      if (!groups[key]) groups[key] = { records: [], sortKey: key };
      groups[key].records.push(r);
    });

    const scatterData = Object.entries(groups).map(([group, { records, sortKey }]) => {
      const xVal = calculateStat(records, config.xAxisFieldId || 'ROOT_COUNT', activeAggregation);
      const yVal = calculateStat(records, config.yAxisFieldId || 'ROOT_COUNT', activeAggregation);
      const zVal = config.zAxisFieldId ? calculateStat(records, config.zAxisFieldId, activeAggregation) : records.length * 10;

      return {
          name: group,
          x: Number(xVal.toFixed(2)),
          y: Number(yVal.toFixed(2)),
          z: Number(zVal.toFixed(2)),
          _sortKey: sortKey
      };
    });

    scatterData.sort((a, b) => String(a._sortKey).localeCompare(String(b._sortKey)));
    return {
        data: scatterData.map(({ _sortKey, ...rest }) => rest),
        seriesKeys: ['y'],
        xAxisKey: 'x',
        zAxisKey: 'z'
    };
  }

  // Normal Charts (Bar, Line, Area, Pie, etc)
  const isDateAxis = config.xAxisFieldId === 'startedAt';

  // Pivot structure: { xAxisValue: { sortKey, series1: val1, series2: val2, ... } }
  const groups: Record<string, { recordsBySeries: Record<string, any[]>, sortKey: number | string }> = {};
  const seriesKeySet = new Set<string>();

  validResponses.forEach(r => {
    // 1. Determine X-Axis Value
    let xVal: any;
    let sortKey: number | string;

    if (isDateAxis) {
      const d = new Date(r.startedAt);
      
      if (isNaN(d.getTime())) {
          xVal = "Data Inválida";
          sortKey = 0;
      } else {
          if (config.dateGrouping === 'day' || !config.dateGrouping) {
              xVal = format(d, 'dd/MM/yyyy');
              sortKey = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
          } else if (config.dateGrouping === 'week') {
              const startOfW = new Date(d);
              startOfW.setDate(d.getDate() - d.getDay()); // Sunday
              xVal = `Sem ${format(startOfW, 'dd/MM')}`;
              sortKey = startOfW.getTime();
          } else if (config.dateGrouping === 'month') {
              xVal = format(d, 'MM/yyyy');
              sortKey = new Date(d.getFullYear(), d.getMonth(), 1).getTime();
          } else if (config.dateGrouping === 'bimonth') {
              const m = d.getMonth();
              const bm = Math.floor(m / 2) + 1;
              xVal = `${bm}º Bim/${d.getFullYear()}`;
              sortKey = new Date(d.getFullYear(), (bm - 1) * 2, 1).getTime();
          } else if (config.dateGrouping === 'semester') {
              const m = d.getMonth();
              const sem = Math.floor(m / 6) + 1;
              xVal = `${sem}º Sem/${d.getFullYear()}`;
              sortKey = new Date(d.getFullYear(), (sem - 1) * 6, 1).getTime();
          } else if (config.dateGrouping === 'year') {
              xVal = format(d, 'yyyy');
              sortKey = new Date(d.getFullYear(), 0, 1).getTime();
          } else {
              xVal = format(d, 'dd/MM/yyyy');
              sortKey = d.getTime();
          }
      }
    } else {
      if (config.xAxisFieldId === 'respondentName') xVal = r.respondentName;
      else if (config.xAxisFieldId === 'formId') xVal = forms?.find(x => x.id === r.formId)?.title || r.formId;
      else xVal = getFieldValue(r, config.xAxisFieldId);

      if (xVal === undefined || xVal === null || xVal === '') xVal = 'N/A';
      xVal = String(xVal);
      sortKey = xVal;
    }

    // 2. Determine Series/Pivot Key
    let seriesKey = 'y'; // default
    if (config.seriesAxisFieldId) {
      if (config.seriesAxisFieldId === 'respondentName') seriesKey = r.respondentName || 'N/A';
      else if (config.seriesAxisFieldId === 'formId') seriesKey = forms?.find(x => x.id === r.formId)?.title || r.formId || 'N/A';
      else {
        const val = getFieldValue(r, config.seriesAxisFieldId);
        seriesKey = (val !== undefined && val !== null && val !== '') ? String(val) : 'N/A';
      }
    }

    seriesKeySet.add(seriesKey);

    // 3. Populate Group
    const xKey = String(xVal);
    if (!groups[xKey]) {
        groups[xKey] = { recordsBySeries: {}, sortKey };
    }
    if (!groups[xKey].recordsBySeries[seriesKey]) {
        groups[xKey].recordsBySeries[seriesKey] = [];
    }

    groups[xKey].recordsBySeries[seriesKey].push(r);
  });

  const seriesKeys = Array.from(seriesKeySet);

  // 4. Flatten and calculate
  const chartData = Object.entries(groups).map(([xVal, { recordsBySeries, sortKey }]) => {
      const point: SerializedDataPoint = { x: xVal, _sortKey: sortKey };

      // se nao for agregado, tecnicamente deveríamos retornar múltiplas linhas por X,
      // mas na prática, se for 'NONE', ou o xVal já é único, ou os múltiplos serão somados/esmagados
      // se a UI não tratar múltiplos X iguais.
      // Para manter a API limpa, 'NONE' ainda calculará um proxy (ex. o primeiro ou último) ou fará COUNT.
      // O requisito diz: "Agregação Opcional. Se não agregado, plotar exatamente como as linhas entram."
      // Para fazer isso perfeitamente, precisaríamos não agrupar.

      seriesKeys.forEach(sk => {
          const records = recordsBySeries[sk] || [];
          if (records.length === 0) {
              point[sk] = null; // Important: Fill gaps with strictly null
          } else {
              if (activeAggregation === 'NONE') {
                  // Se não há agregação, pegamos o valor da primeira ocorrencia
                  // e se houver múltiplas para o mesmo X e mesma série, é uma anomalia
                  // sem agregação. Vamos pegar o primeiro.
                  const rawVal = getFieldValue(records[0], config.yAxisFieldId || 'ROOT_COUNT');
                  point[sk] = Number(rawVal);
                  if(isNaN(point[sk])) point[sk] = rawVal || null; // Fallback to raw string if not a number
              } else {
                  const val = calculateStat(records, config.yAxisFieldId || 'ROOT_COUNT', activeAggregation);
                  if (typeof val === 'string' && isTimeString(val)) {
                      if (vizType !== 'table' && vizType !== 'kpi') {
                          point[sk] = Number((timeToSeconds(val) / 3600).toFixed(2));
                      } else {
                          point[sk] = val;
                      }
                  } else {
                      point[sk] = Number(Number(val).toFixed(2));
                  }
              }
          }
      });
      return point;
  });

  // Sort
  if (isDateAxis) {
      chartData.sort((a, b) => (a._sortKey as number) - (b._sortKey as number));
  } else {
      chartData.sort((a, b) => String(a._sortKey).localeCompare(String(b._sortKey)));
  }

  // Remove sortKey
  const finalData = chartData.map(({ _sortKey, ...rest }) => rest);

  return {
      data: finalData,
      seriesKeys: seriesKeys.filter(k => k !== 'x' && k !== '_sortKey'), // ensure no collisions
      xAxisKey: 'x'
  };
}
